/* eslint-disable @typescript-eslint/no-explicit-any */
import { axiosInstance, axiosInstanceV2, axiosInstanceV3 } from '../axios';
import { ICategory } from '../categories/types';
import { IActivity, IBook, PaginatedResponse, Params, ReadingBooks, ReceivedBooks, ReceivedBooksMonth } from '../types';
import { getPaginatedEndpoint, snakeCaseDictToCamelCaseDict } from '../utils';

export function getBookActivities(): Promise<PaginatedResponse<IActivity>> {
  return getPaginatedEndpoint(`/bookstore/activities/`, {}, axiosInstanceV2);
}

export function getBookCategories(): Promise<PaginatedResponse<ICategory>> {
  return getPaginatedEndpoint(`/bookstore/categories/?limit=99`, {}, axiosInstanceV2);
}

export function getBooksMonth(params: Record<string, string> = {}): Promise<PaginatedResponse<IBook>> {
  return getPaginatedEndpoint(`/bookstore/books/monthly/`, params, axiosInstanceV3);
}

export function getBooks(params: Record<string, string> = {}): Promise<PaginatedResponse<IBook>> {
  return getPaginatedEndpoint(`/bookstore/books/`, params, axiosInstanceV3);
}

export function getReadingBooks(params: Record<string, string> = {}): Promise<PaginatedResponse<IBook>> {
  return getPaginatedEndpoint(`/bookstore/reading-books/`, params, axiosInstanceV3);
}

export function getReceivedBooksMonth(params: Record<string, string> = {}): Promise<ReceivedBooksMonth[]> {
  return axiosInstanceV3
    .get(`bookstore/received-books/monthly-report/`, { params })
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as ReceivedBooksMonth[]);
}

export function getMonthBestSellers(params: Record<string, string> = {}): Promise<PaginatedResponse<IBook>> {
  return axiosInstanceV3.get(`bookstore/books/monthly-best-sellers/`, { params }).then(({ data }) => snakeCaseDictToCamelCaseDict(data));
}

export function getReceivedBooksMonthBestSellers(params: Record<string, string> = {}): Promise<ReceivedBooksMonth[]> {
  return axiosInstanceV3
    .get(`bookstore/books/best-seller-monthly-report/`, { params })
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as ReceivedBooksMonth[]);
}

export function getReceivedBooks(params: Record<string, string> = {}): Promise<PaginatedResponse<ReceivedBooks>> {
  return getPaginatedEndpoint(`bookstore/received-books/`, { params }, axiosInstanceV3);
}

export function getMyBooks(params: Record<string, string> = {}): Promise<PaginatedResponse<IBook>> {
  return getPaginatedEndpoint(`/bookstore/my-books/`, params, axiosInstanceV2);
}

export async function getBooksDetail(bookID: string): Promise<IBook> {
  return axiosInstanceV3
    .get(`/bookstore/books/${bookID}/`)
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as IBook);
}

export async function getReadingBooksDetail(bookID: string): Promise<ReadingBooks> {
  return axiosInstanceV3
    .get(`/bookstore/reading-books/${bookID}/detail-by-book/`)
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as ReadingBooks);
}

export async function getMyBooksDetail(bookID: string): Promise<IBook> {
  return axiosInstanceV3
    .get(`/bookstore/received-books/${bookID}/`)
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as IBook);
}

export function getExchangeableBooks(params: Record<string, string> = {}): Promise<PaginatedResponse<IBook>> {
  return getPaginatedEndpoint(`/bookstore/received-books/${params.bookUserId}/exchangeable-books`, params, axiosInstanceV3);
}

export function getFavoritedBooks(params: Params = {}): Promise<PaginatedResponse<IBook>> {
  return getPaginatedEndpoint(`/bookstore/favourite-books`, params, axiosInstanceV3);
}

export async function getStreamBook(bookId: number): Promise<any> {
  return axiosInstanceV3
    .get(`/bookstore/books/${bookId}/read-stream/`, {
      responseType: 'blob',
    })
    .then(({ data, headers }) => ({ book: data, contentType: headers['content-type'] }));
}

export async function getDownloadURL(bookId: number): Promise<string> {
  return axiosInstanceV3.get(`/bookstore/books/${bookId}/download/`).then(({ data }) => data);
}

export async function setReadingBook(bookId: number): Promise<IBook> {
  return axiosInstanceV3
    .post(`/bookstore/reading-books/`, {
      book: bookId,
    })
    .then(({ data }) => data?.book);
}

export function sendBookAsEmail(data: IBookIDData & { email: string; agreement: string }) {
  return axiosInstanceV2.get(`/bookstore/send-book/${data.bookID}?email=${data.email}&agreement_id=${data.agreement}`);
}

export function unFavoriteBook(bookId: number) {
  return axiosInstanceV3.delete(`bookstore/favourite-books/${bookId}/`);
}

export function favoriteBook(bookId: number) {
  return axiosInstanceV3.post(`bookstore/favourite-books/`, { book: bookId });
}

export function exchangeBook(data: { currentBookUserId: string; newBookId: string }) {
  return axiosInstanceV3.post(`/bookstore/received-books/${data.currentBookUserId}/exchange/`, { book_id: data.newBookId });
}

export function updateSubscription(id: number, data: any) {
  return axiosInstance.put(`/subscription/my-subscriptions/${id}/`, data);
}

interface IBookIDData {
  bookID: number;
}
